import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import 'odometer/themes/odometer-theme-default.css';

import Spinner from '../../../../shared/atoms/Spinner';
import PercentageBar from '../PercentageBar';
import CustomTooltip from '../../../../shared/atoms/CustomTooltip';
import { useUnits } from '../../../../../hooks/useUnits';
import { buildUnitLabels } from '../../../../../utils/buildUnits';
import { useConfirmation } from '../../../../../context/ConfirmationContext';
import { putCRUD } from '../../../../../services/crudService';
import { useGeneralContext } from '../../../../../context/GeneralContext';
import RefreshButton from '../../../../shared/molecules/RefreshButton';
import MoreOptionsMenu from '../../../../shared/molecules/MoreOptionsMenu';

const PartDisplay = ({
  part,
  isLoading,
  onClose,
  isAssetDetails,
  refetchParts,
  hideMoreOptions,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: unitData } = useUnits();
  const units = useMemo(() => buildUnitLabels(unitData, t), [t, unitData]);
  const { openConfirmation } = useConfirmation();
  const { openToast } = useGeneralContext();
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = useCallback(async () => {
    openConfirmation({
      message: t(
        'machines.facilities.center_pane.service_life.part_reset_confirmation',
        { part: part.name },
      ),
      handleAction: async () => {
        try {
          setIsResetting(true);
          await putCRUD({ entity: 'part', id: part.id, data: { value: 0 } });
          refetchParts();
          openToast(
            t('machines.facilities.center_pane.service_life.part_reset'),
          );
        } catch (error) {
          console.error(error);
          openToast(
            t('machines.facilities.center_pane.service_life.part_reset_error'),
            'error',
          );
        } finally {
          setIsResetting(false);
        }
      },
      title: t('machines.facilities.center_pane.service_life.reset_part', {
        part: part.name,
      }),
    });
  }, [openConfirmation, openToast, part.id, part.name, refetchParts, t]);

  const menuOptions = useMemo(() => {
    const opt = [
      {
        label: t('machines.facilities.center_pane.service_life.go_to_config'),
        action: () =>
          history.push(`/portal/management/parts/${part.id}/manage`),
      },
    ];
    if (isAssetDetails)
      opt.unshift({
        label: t('machines.facilities.center_pane.service_life.go_to_parts'),
        action: () =>
          history.push(`/portal/maps/facilities/${part.asset}/parts`),
      });
    return opt;
  }, [history, isAssetDetails, part.asset, part.id, t]);

  return (
    <Card>
      {isLoading ? (
        <Box
          sx={{
            maxHeight: '364px',
            minHeight: '364px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Spinner />
        </Box>
      ) : (
        <Stack
          style={{
            padding: '16px',
            maxHeight: '364px',
            minHeight: '364px',
            color: 'white',
          }}
        >
          <CardHeader
            sx={{ padding: 0 }}
            action={
              onClose && (
                <CustomTooltip title={t('modal.close')}>
                  <IconButton onClick={onClose}>
                    <Close sx={{ color: 'white' }} />
                  </IconButton>
                </CustomTooltip>
              )
            }
            title={
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
              >
                {!hideMoreOptions && <MoreOptionsMenu options={menuOptions} />}
                <Typography
                  fontSize="20px"
                  fontWeight={500}
                  width="100%"
                  textAlign="center"
                >
                  {part.name}
                </Typography>
              </Stack>
            }
          />

          <Stack spacing={4} margin="auto">
            <Stack justifyContent="center" alignItems="center">
              <Typography>
                {t(
                  'machines.facilities.center_pane.service_life.life_remaining',
                )}
              </Typography>
              <Stack direction="row">
                <Typography variant="h4">
                  {part.percentage.toFixed(2)}%
                </Typography>
                {refetchParts && (
                  <RefreshButton
                    refreshAction={handleReset}
                    isRefreshing={isResetting}
                    title={t(
                      'machines.facilities.center_pane.service_life.reset_part',
                      { part: part.name },
                    )}
                  />
                )}
              </Stack>
              <PercentageBar
                aria-label={`Percentage bar ${part.percentage}%`}
                statusPercentage={part.percentage}
                showPercentage={false}
                threshold={part.threshold}
                limit_value={part.limit_value}
              />
            </Stack>
            <Stack justifyContent="center" alignItems="center">
              <Typography>
                {t('machines.facilities.center_pane.service_life.value')}
              </Typography>
              <Typography>
                {part.value} {units[part.unit]}
              </Typography>
            </Stack>
            <Stack justifyContent="center" alignItems="center">
              <Typography>
                {t(
                  'machines.facilities.center_pane.service_life.expected_life',
                )}
              </Typography>
              <Typography>
                {part.limit_value} {units[part.unit]}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Card>
  );
};

PartDisplay.defaultProps = {
  isLoading: false,
  isAssetDetails: false,
  onClose: null,
  hideMoreOptions: false,
};

PartDisplay.propTypes = {
  part: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  isAssetDetails: PropTypes.bool,
  hideMoreOptions: PropTypes.bool,
  onClose: PropTypes.func,
  refetchParts: PropTypes.func,
};

export default PartDisplay;
